import React, { useState, useEffect, useRef } from 'react';
import Text from 'components/Text/Text';
import { CopyOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import style from './ChunkOfTextWithCopying.module.scss';

interface IProps {
	text: string;
	className?: string;
	isNewTooltip?: boolean;
}

const ChunkOfTextWithCopying: React.FC<IProps> = ({ text, className, isNewTooltip = false }) => {
	const [showIcon, setShowIcon] = useState(false);
	const timeout = useRef<null | ReturnType<typeof setTimeout>>(null);
	const containerClasses = classNames(style.container, className);
	const iconClasses = classNames(style.iconClose, { [style.showIcon]: showIcon });

	const chunkOfText = `...${text.slice(-4)}`;

	const unsecuredCopyToClipboard = async () => {
		const textArea = document.createElement('textarea');
		textArea.value = text;
		textArea.style.position = 'absolute';
		textArea.style.left = '-99999px';
		document.body.appendChild(textArea);
		textArea.focus();
		textArea.select();
		try {
			document.execCommand('copy');
			if (!showIcon) {
				setShowIcon(true);
				timeout.current = setTimeout(() => {
					setShowIcon(false);
				}, 2000);
			}
		} catch (error) {
			console.error('Error in copying text: ', error);
		}
		document.body.removeChild(textArea);
	};

	const handleCopyAllText = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		e.stopPropagation();
		if (window.isSecureContext && navigator.clipboard) {
			try {
				navigator.clipboard.writeText(text); // navigator.clipboard.writeText(text) not working on insecure(http) connection
				if (!showIcon) {
					setShowIcon(true);
					timeout.current = setTimeout(() => {
						setShowIcon(false);
					}, 2000);
				}
			} catch (error) {
				console.error('Error in copying text: ', error);
			}
		} else {
			unsecuredCopyToClipboard();
		}
	};

	useEffect(() => {
		return () => {
			if (timeout.current) {
				clearTimeout(timeout.current);
			}
		};
	}, []);

	return (
		<div className={containerClasses}>
			<CopyOutlined className={iconClasses} />
			<div className={style.textWrapper} onClick={handleCopyAllText}>
				<div className={style.chunkOfText}>{chunkOfText}</div>
				<Text
					className={style.textComponent}
					translationKey={text}
					withTooltip={true}
					isNewTooltip={isNewTooltip}
				/>
			</div>
		</div>
	);
};

export default React.memo(ChunkOfTextWithCopying);
